import React from "react";
import { MenuItem } from "../../context/pack";
import ProductCard from "../../../../components/product/product-card";
import { getCDNResizedImageUrl } from "../../../../utils/get-cdn-resized-image-url";
import { ProductMenu } from "../../../../__generated__/graphql";

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  packs: Array<{
    items: MenuItem[];
    quantity?: number;
    isSampleMenu?: boolean;
  }>;
  closingTime: string;
  storeName: string;
  notice?: string;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  packs,
  closingTime,
  storeName,
  notice,
}) => {
  if (!isOpen) return null;

  const calculateTotalPrice = (pack: MenuItem[]): number => {
    return pack.reduce(
      (total, item) => total + item.price * (item.quantity || 1),
      0
    );
  };

  const calculateDiscountedPrice = (pack: MenuItem[]): number => {
    const discountRate = pack[0]?.discountRate ?? 0;
    const totalPrice = calculateTotalPrice(pack);
    return totalPrice - totalPrice * (discountRate / 100);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
  };

  const handleProductClick = (product: {
    id: number;
    name: string;
    userPrice: number;
    price: number;
  }) => {
    // 미리보기에서는 클릭 동작을 비활성화
    return;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">소비자 미리보기</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* 매장 정보 */}
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">{storeName}</h3>
          <div className="flex items-center mt-2 text-sm text-gray-600">
            <svg
              className="w-4 h-4 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                clipRule="evenodd"
              />
            </svg>
            <span>픽업 가능 시간: {formatTime(closingTime)}까지</span>
          </div>
        </div>

        {/* 매장안내 */}
        {notice && (
          <div className="mx-4 my-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="mb-2">
              <span className="text-gray-800 font-bold text-sm">
                📢 매장안내:
              </span>
            </div>
            <p className="text-gray-800 text-sm whitespace-pre-wrap">
              {notice}
            </p>
          </div>
        )}

        {/* 상품 목록 */}
        <div className="p-4 space-y-4">
          {packs.map((pack, index) => {
            const packName = pack.items.map((item) => item.name).join(" + ");
            const totalPrice = calculateTotalPrice(pack.items);
            const discountedPrice = calculateDiscountedPrice(pack.items);

            // ProductCard에 맞는 형태로 데이터 변환
            // img와 menus는 양립할 수 없음
            let productImg: string | null = null;
            let productMenus: ProductMenu[] | null = null;

            if (pack.items[0].id !== 0) {
              // 1️⃣메뉴 선택: menus 배열 생성, img는 null
              productMenus = pack.items.map((item, itemIndex) => ({
                __typename: "ProductMenu" as const,
                id: itemIndex + 1,
                img:
                  item.img instanceof File
                    ? URL.createObjectURL(item.img)
                    : getCDNResizedImageUrl(item.img, {
                        width: 600,
                        quality: 80,
                      }),
                menu: {
                  __typename: "Menu" as const,
                  createdAt: new Date().toISOString(),
                  id: item.id,
                  img:
                    item.img instanceof File
                      ? URL.createObjectURL(item.img)
                      : getCDNResizedImageUrl(item.img, {
                          width: 600,
                          quality: 80,
                        }),
                  isDeleted: false,
                  name: item.name,
                  price: item.price,
                  updatedAt: new Date().toISOString(),
                  store: {} as any,
                },
                menuId: item.id,
                quantity: item.quantity || 1,
                product: {} as any,
                productId: index + 1,
              }));
            } else {
              // 2️⃣직접 입력: img만 설정, menus는 null
              productImg =
                pack.items[0].img instanceof File
                  ? URL.createObjectURL(pack.items[0].img)
                  : pack.items[0].img;
            }

            const productData = {
              id: index + 1,
              name: packName,
              img: productImg,
              menus: productMenus,
              price: totalPrice,
              userPrice: discountedPrice,
              quantity: pack.quantity || 1,
              isEnd: false,
              isSampleMenu: pack.isSampleMenu || false,
              isDeleted: false,
              isSoldout: false,
              currentCount: pack.quantity || 1,
            };

            return (
              <div key={index}>
                <ProductCard
                  product={productData}
                  onClickProduct={handleProductClick}
                />
              </div>
            );
          })}
        </div>

        {/* 푸터 */}
        <div className="sticky bottom-0 bg-gray-50 p-4 border-t border-gray-200">
          <div className="text-center text-sm text-gray-600">
            <p>이 화면이 소비자에게 보이는 모습입니다</p>
            <p className="mt-1">픽업 시간: {formatTime(closingTime)}까지</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
